import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Heart, Clock, Shield, Sparkles, RefreshCw, ChevronLeft, ChevronRight, Check, X, ShieldCheck, Droplets, Zap, Settings2, MessageSquare, MessageCircle, Users, Layers, Recycle, Truck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ServiceCard from "@/components/ServiceCard";
import PostalCodeInput from "@/components/PostalCodeInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n";
import { CANTONS } from "@/lib/cantons";
import Seo from "@/components/Seo";
import { buildLocalBusinessLd, buildOrganizationLd, DEFAULT_META } from "@/lib/seo";

// Images Pexels pour les 8 nouveaux services Mopper (déménagement, débarras…)
const CARD_IMG_MOVE = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/qedruq3t_478f1f53-dc1e-45fd-83c7-0f0f91fede73.png";
const CARD_IMG_CLEAR = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/vwjwaf6b_ChatGPT%20Image%2011%20juin%202026%2C%2000_10_18.png";
const CARD_IMG_DECLUTTER = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/afko4rp6_ChatGPT%20Image%2010%20juin%202026%2C%2023_49_37.png";
const CARD_IMG_ORGANIZE = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/6rxlcq28_ChatGPT%20Image%2011%20juin%202026%2C%2000_32_59.png";
const CARD_IMG_CLEAN = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/5heg7dg6_image.png";
const CARD_IMG_RECYCLE = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/8mpd0m48_ChatGPT%20Image%2011%20juin%202026%2C%2000_38_25.png";
const CARD_IMG_ASSEMBLY = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/qj5x12tc_ChatGPT%20Image%2011%20juin%202026%2C%2000_29_08.png";
const CARD_IMG_TRANSPORT = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/krvb1wax_ChatGPT%20Image%2011%20juin%202026%2C%2000_45_51.png";
const IMG_FEATURES = CARD_IMG_MOVE;
const IMG_ADVANTAGES = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/9hfblbh7_ChatGPT%20Image%2011%20juin%202026%2C%2001_37_19.png";
const IMG_COMPARE = "https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/9lf20elh_ChatGPT%20Image%2011%20juin%202026%2C%2001_33_46.png";
const IMG_COMPARE_BEFORE = "https://customer-assets-rejwkqb3.emergentagent.net/job_mopper-reserve/artifacts/4a06669e5a6f4f4a_IMG_7465.jpeg";
const IMG_COMPARE_AFTER = "https://customer-assets-rejwkqb3.emergentagent.net/job_mopper-reserve/artifacts/a8e647796c9eb200_IMG_7464.jpeg";

/* ---------- Config : RC professionnelle. coverageAmount = null tant qu'il n'est pas confirmé. */
const RC_PROFESSIONAL = { enabled: true, coverageAmount: null };

/* ---------- Icônes emoji des étapes réellement utilisées par le suivi (tracker.py) ---------- */
const SUIVI_STEP_ICONS = {
  planned: "📅",
  team_preparing: "👷",
  team_en_route: "🚐",
  arrived_onsite: "📍",
  completed: "🎉",
};

/* ---------- Libellés i18n des carrousels (aria + "À partir de") ---------- */
const CAROUSEL_UI = {
  fr: { prev: "Précédent", next: "Suivant", goOffer: "Voir l'offre", goCanton: "Aller au canton", goService: "Aller au service", goCard: "Aller à la carte", from: "À partir de", flag: "Drapeau" },
  de: { prev: "Zurück", next: "Weiter", goOffer: "Angebot ansehen", goCanton: "Zum Kanton", goService: "Zur Leistung", goCard: "Zur Karte", from: "Ab", flag: "Flagge" },
  en: { prev: "Previous", next: "Next", goOffer: "View offer", goCanton: "Go to canton", goService: "Go to service", goCard: "Go to card", from: "From", flag: "Flag" },
};
function useCarouselUi() { const { lang } = useI18n(); return CAROUSEL_UI[lang] || CAROUSEL_UI.fr; }

/* ---------- PacksScroller — 5 offres Mopper, scroll horizontal + dots ---------- */
function PacksScroller({ packs, navigate }) {
  const cu = useCarouselUi();
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);

  function updateState() {
    const el = scrollerRef.current;
    if (!el) return;
    setNeedsScroll(el.scrollWidth > el.clientWidth + 8);
    const cards = Array.from(el.querySelectorAll("[data-pack-card]"));
    if (!cards.length) return;
    const left = el.getBoundingClientRect().left;
    let best = 0, bestDist = Infinity;
    cards.forEach((c, i) => {
      const d = Math.abs(c.getBoundingClientRect().left - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  }

  useEffect(() => {
    updateState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, []);

  function scrollTo(i) {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll("[data-pack-card]"));
    const card = cards[i];
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  }

  return (
    <>
      <div className="mx-auto" style={{ maxWidth: "1760px" }}>
        <div
          ref={scrollerRef}
          className="no-scrollbar flex gap-4 md:gap-6 overflow-x-auto snap-x snap-proximity px-6 sm:px-8 md:px-10 lg:px-14 scroll-pl-6 sm:scroll-pl-8 md:scroll-pl-10 lg:scroll-pl-14 pb-4 overscroll-x-contain"
          data-testid="packs-row"
        >
          {packs.map((p, i) => (
            <article
              key={i}
              data-pack-card
              className={`snap-start shrink-0 w-[78vw] sm:w-[44vw] md:w-[360px] lg:w-[420px] rounded-3xl p-7 md:p-9 flex flex-col shadow-[0_15px_30px_-12px_rgba(10,15,44,0.25)] ${
                p.featured ? "bg-[#0A0F2C] text-white" : "bg-white text-[#0A0F2C]"
              }`}
              data-testid={`pack-card-${i}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] ${
                  p.featured ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-[#EAF0FF] text-[#2D5BFF]"
                }`}>
                  {p.tag}
                </span>
              </div>
              <h3
                className={`mt-5 font-extrabold text-3xl md:text-4xl tracking-tighter leading-[0.95] ${
                  p.featured ? "text-white" : "text-[#0A0F2C]"
                }`}
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {p.name}
              </h3>
              <p className={`mt-3 text-sm md:text-[15px] ${p.featured ? "text-white/85" : "text-[#4B5563]"}`}>{p.desc}</p>
              <ul className={`mt-5 flex flex-col gap-2 text-sm ${p.featured ? "text-white/95" : "text-[#0A0F2C]"}`}>
                {p.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className={`mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 ${
                      p.featured ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-[#EAF0FF] text-[#2D5BFF]"
                    }`}>
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => navigate("/devis")}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-bold text-sm active:scale-[0.98] transition-all ${
                  p.featured
                    ? "bg-[#FFD84D] text-[#0A0F2C] hover:bg-white"
                    : "bg-[#0A0F2C] text-white hover:bg-[#2D5BFF]"
                }`}
                data-testid={`pack-cta-${i}`}
              >
                {p.cta} <ArrowRight size={14} />
              </button>
            </article>
          ))}
        </div>

        {needsScroll && (
          <div className="mt-4 flex items-center justify-center gap-2.5" data-testid="packs-dots">
            {packs.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`${cu.goOffer} ${i + 1}`}
                onClick={() => scrollTo(i)}
                className={`h-3 rounded-full transition-all duration-300 ease-out ${
                  active === i ? "w-12 bg-[#2D5BFF]" : "w-3 bg-gray-300 hover:bg-gray-500"
                }`}
                data-testid={`packs-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- CantonsScroller — 6 cantons romands, scroll horizontal + dots ---------- */
function CantonsScroller({ items, lang, dotColor = "white" }) {
  const cu = CAROUSEL_UI[lang] || CAROUSEL_UI.fr;
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);

  function updateState() {
    const el = scrollerRef.current;
    if (!el) return;
    setNeedsScroll(el.scrollWidth > el.clientWidth + 8);
    const cards = Array.from(el.querySelectorAll("[data-canton-card]"));
    if (cards.length) {
      const left = el.getBoundingClientRect().left;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect().left - left);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActive(best);
    }
  }

  useEffect(() => {
    updateState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, []);

  function scrollTo(i) {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll("[data-canton-card]"));
    const card = cards[i];
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  }

  const isOnBlue = dotColor === "white";
  return (
    <>
      <div className="mx-auto" style={{ maxWidth: "1760px" }}>
        <div
          ref={scrollerRef}
          className="no-scrollbar flex gap-4 md:gap-6 overflow-x-auto xl:overflow-visible snap-x snap-proximity xl:snap-none px-6 sm:px-8 md:px-10 lg:px-14 scroll-pl-6 sm:scroll-pl-8 md:scroll-pl-10 lg:scroll-pl-14 pb-4 xl:pb-0 overscroll-x-contain"
          data-testid="zone-row"
        >
          {items.map((c) => (
            <article
              key={c.code}
              data-canton-card
              className="snap-start shrink-0 w-[62vw] sm:w-[40vw] md:w-[30vw] lg:w-[23vw] xl:flex-1 xl:w-auto xl:min-w-0 rounded-2xl bg-white shadow-[0_10px_25px_-12px_rgba(10,15,44,0.25)] overflow-hidden flex flex-col"
              data-testid={`zone-canton-${c.code}`}
            >
              <div className="h-40 sm:h-44 md:h-48 lg:h-52 bg-gray-50 flex items-center justify-center p-4 md:p-6 overflow-hidden">
                <img
                  src={c.flag}
                  alt={`${cu.flag} ${c.name[lang] || c.name.fr}`}
                  className="block max-w-full max-h-full object-contain"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-extrabold text-[15px] text-[#0A0F2C] tracking-tight truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {c.name[lang] || c.name.fr}
                  </div>
                  <div className="text-[11px] text-[#9CA3AF] font-semibold truncate">{c.capital}</div>
                </div>
                <span className="ml-2 inline-flex items-center justify-center text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#2D5BFF] bg-[#EAF0FF] rounded-full px-2 py-1 shrink-0">
                  {c.code.toUpperCase()}
                </span>
              </div>
            </article>
          ))}
        </div>

        {/* Dots — only visible when content overflows (typically mobile/sm) */}
        {needsScroll && (
          <div className="mt-6 flex items-center justify-center gap-2.5" data-testid="zone-dots">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`${cu.goCanton} ${i + 1}`}
                onClick={() => scrollTo(i)}
                className={`h-3 rounded-full transition-all duration-300 ease-out ${
                  active === i
                    ? `w-12 ${isOnBlue ? "bg-[#FFD84D]" : "bg-[#2D5BFF]"}`
                    : `w-3 ${isOnBlue ? "bg-white/35 hover:bg-white/60" : "bg-gray-300 hover:bg-gray-500"}`
                }`}
                data-testid={`zone-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- ServicesStackScroller — horizontal scroll with arrows + dots ---------- */
function ServicesStackScroller({ items, navigate }) {
  const cu = useCarouselUi();
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  function updateState() {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanPrev(scrollLeft > 8);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 8);
    // Nearest snap card index
    const cards = Array.from(el.querySelectorAll("[data-stack-card]"));
    if (cards.length) {
      const left = el.getBoundingClientRect().left;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect().left - left);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActive(best);
    }
  }

  useEffect(() => {
    updateState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, []);

  function scrollByDir(dir) {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-stack-card]");
    const step = card ? card.getBoundingClientRect().width + 28 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  }

  function scrollTo(i) {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll("[data-stack-card]"));
    const card = cards[i];
    if (!card) return;
    const target = card.offsetLeft - el.offsetLeft;
    el.scrollTo({ left: target, behavior: "smooth" });
  }

  return (
    <div className="relative">
      {/* Arrows */}
      <button
        type="button"
        onClick={() => scrollByDir(-1)}
        disabled={!canPrev}
        aria-label={cu.prev}
        className="hidden md:inline-flex items-center justify-center absolute -left-2 lg:-left-4 top-[35%] -translate-y-1/2 w-12 h-12 rounded-full bg-white border border-gray-200 shadow-[0_10px_28px_-6px_rgba(10,15,44,0.18)] text-[#0A0F2C] hover:bg-[#2D5BFF] hover:text-white hover:border-transparent transition-all disabled:opacity-0 disabled:pointer-events-none z-10"
        data-testid="services-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={() => scrollByDir(1)}
        disabled={!canNext}
        aria-label={cu.next}
        className="hidden md:inline-flex items-center justify-center absolute -right-2 lg:-right-4 top-[35%] -translate-y-1/2 w-12 h-12 rounded-full bg-white border border-gray-200 shadow-[0_10px_28px_-6px_rgba(10,15,44,0.18)] text-[#0A0F2C] hover:bg-[#2D5BFF] hover:text-white hover:border-transparent transition-all disabled:opacity-0 disabled:pointer-events-none z-10"
        data-testid="services-next"
      >
        <ChevronRight size={20} />
      </button>

      <div
        ref={scrollerRef}
        className="no-scrollbar flex gap-5 md:gap-7 overflow-x-auto snap-x snap-mandatory scroll-pl-2 sm:scroll-pl-3 md:scroll-pl-4 lg:scroll-pl-6 -mx-2 sm:-mx-3 md:-mx-4 lg:-mx-6 px-6 sm:px-8 md:px-10 lg:px-14 pb-4 scroll-smooth"
        data-testid="services-grid"
      >
        {items.map((s, i) => (
          <div
            key={i}
            data-stack-card
            className="snap-start shrink-0 w-[88vw] sm:w-[60vw] md:w-[calc((100%-1.75rem)/2)] xl:w-[calc((100%-3.5rem)/3)] flex"
          >
            <ServiceStackCard {...s} navigate={navigate} />
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="mt-6 flex items-center justify-center gap-2.5" data-testid="services-dots">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${cu.goService} ${i + 1}`}
            onClick={() => scrollTo(i)}
            className={`h-3 rounded-full transition-all duration-300 ease-out ${active === i ? "w-12 bg-[#2D5BFF]" : "w-3 bg-gray-300 hover:bg-gray-500"}`}
            data-testid={`services-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------- ServiceStackCard — Batmaid-inspired card ---------- */
function ServiceStackCard({ img, data, to, testId, navigate, kind }) {
  const cu = useCarouselUi();
  const isB2B = kind === "b2b";
  return (
    <article
      className="group relative rounded-[28px] overflow-hidden bg-white border border-[#0A0F2C]/[0.06] shadow-[0_20px_50px_-25px_rgba(10,15,44,0.12)] hover:shadow-[0_30px_60px_-25px_rgba(10,15,44,0.22)] hover:-translate-y-1 transition-all duration-300 flex flex-col w-full"
      data-testid={testId}
    >
      {/* Image with strong gradient overlay for text readability */}
      <div className="relative aspect-[16/9] md:aspect-[16/10] overflow-hidden">
        <img
          src={img}
          alt={data.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Strong overlay for legibility */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,44,0.05) 0%, rgba(10,15,44,0.45) 60%, rgba(10,15,44,0.78) 100%)" }} />
        {/* Top-left badge */}
        <span
          className={`absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] backdrop-blur-md ${isB2B ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-white/95 text-[#0A0F2C]"}`}
          data-testid={`${testId}-badge`}
        >
          {data.tag}
        </span>
        {/* Title overlay (also visible inside card for clarity) */}
        <div className="absolute bottom-4 left-5 right-5">
          <h3
            className="font-extrabold text-white text-2xl md:text-3xl lg:text-[34px] tracking-tighter leading-[1.05] drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {data.title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6 md:p-8">
        <p className="text-[15px] md:text-base text-[#4B5563] leading-relaxed">{data.desc}</p>

        {/* 3 bullets — homogeneous icons */}
        <ul className="mt-5 flex flex-col gap-2.5">
          {data.bullets.slice(0, 3).map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm md:text-[15px] text-[#0A0F2C]">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FFD84D] text-[#0A0F2C] shrink-0 mt-0.5">
                <Check size={11} strokeWidth={3} />
              </span>
              <span className="leading-snug font-medium">{b}</span>
            </li>
          ))}
        </ul>

        {/* Equal spacers center the divider between the bullets and the price block */}
        <div className="flex-1 min-h-[24px]" />
        <div className="border-t border-[#0A0F2C]/[0.08]" />
        <div className="flex-1 min-h-[24px]" />

        {/* Price + CTA — always stacked to prevent overlap inside narrow carousel cards */}
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] font-bold whitespace-nowrap">{cu.from}</div>
            <div
              className="font-extrabold text-2xl md:text-3xl text-[#0A0F2C] tracking-tighter leading-none mt-1 whitespace-nowrap"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid={`${testId}-price`}
            >
              {data.price.replace(/^dès\s*/i, "")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(to)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-[#FFB800] text-[#0A0F2C] px-5 py-3 font-extrabold text-sm md:text-[15px] active:scale-[0.98] transition-all hover:shadow-[0_15px_30px_-8px_rgba(255,216,77,0.55)] w-full whitespace-nowrap"
            data-testid={`${testId}-cta`}
          >
            {data.cta} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </article>
  );
}



const featureIcons = [<ShieldCheck size={22} />, <Droplets size={22} />, <Zap size={22} />, <Settings2 size={22} />];

function ServicesCarousel({ items, onDark = false }) {
  const cu = useCarouselUi();
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  function update() {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-carousel-card]");
    if (!cards.length) return;
    const scrollLeft = el.scrollLeft;
    let idx = 0;
    let best = Infinity;
    cards.forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - el.offsetLeft - scrollLeft);
      if (d < best) { best = d; idx = i; }
    });
    setActive(idx);
    setCanPrev(scrollLeft > 8);
    setCanNext(scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scrollByDir(dir) {
    const el = scrollerRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-carousel-card]");
    const step = firstCard ? firstCard.clientWidth + 24 : Math.max(320, el.clientWidth * 0.7);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  }

  function scrollTo(i) {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-carousel-card]");
    if (cards[i]) el.scrollTo({ left: cards[i].offsetLeft - el.offsetLeft, behavior: "smooth" });
  }

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="overflow-x-auto no-scrollbar scroll-smooth scroll-pl-2 sm:scroll-pl-3 md:scroll-pl-4 lg:scroll-pl-6"
        data-testid="services-scroller"
      >
        <div className="flex gap-4 md:gap-6 snap-x snap-mandatory pl-2 sm:pl-3 md:pl-4 lg:pl-6 pr-2 sm:pr-3 md:pr-4 lg:pr-6">
          {items.map((s, i) => (
            <div
              key={i}
              data-carousel-card
              className="shrink-0 snap-start w-[82vw] sm:w-[52vw] md:w-[380px] lg:w-[420px] xl:w-[440px]"
            >
              <ServiceBentoCard img={s.img} data={s.data} to={s.to} testId={s.testId} index={i + 1} />
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => scrollByDir(-1)}
        disabled={!canPrev}
        aria-label={cu.prev}
        className={`hidden md:inline-flex items-center justify-center absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full shadow-[0_10px_28px_-6px_rgba(10,15,44,0.25)] transition-all disabled:opacity-0 disabled:pointer-events-none z-10 ${onDark ? "bg-white/10 border border-white/20 text-white hover:bg-[#FFD84D] hover:text-[#0A0F2C] hover:border-[#FFD84D] backdrop-blur-sm" : "bg-white border border-gray-200 text-[#0A0F2C] hover:bg-[#2D5BFF] hover:text-white hover:border-transparent"}`}
        data-testid="carousel-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={() => scrollByDir(1)}
        disabled={!canNext}
        aria-label={cu.next}
        className={`hidden md:inline-flex items-center justify-center absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full shadow-[0_10px_28px_-6px_rgba(10,15,44,0.25)] transition-all disabled:opacity-0 disabled:pointer-events-none z-10 ${onDark ? "bg-white/10 border border-white/20 text-white hover:bg-[#FFD84D] hover:text-[#0A0F2C] hover:border-[#FFD84D] backdrop-blur-sm" : "bg-white border border-gray-200 text-[#0A0F2C] hover:bg-[#2D5BFF] hover:text-white hover:border-transparent"}`}
        data-testid="carousel-next"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="mt-10 flex items-center justify-center gap-2.5 px-4" data-testid="carousel-dots">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${cu.goCard} ${i + 1}`}
            onClick={() => scrollTo(i)}
            className={`h-3 rounded-full transition-all duration-300 ease-out ${active === i ? (onDark ? "w-12 bg-[#FFD84D]" : "w-12 bg-[#2D5BFF]") : (onDark ? "w-3 bg-white/30 hover:bg-white/60" : "w-3 bg-gray-300 hover:bg-gray-500")}`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function ServiceBentoCard({ img, data, to, testId, index }) {
  return (
    <Link
      to={to}
      className="group relative rounded-3xl overflow-hidden block h-[460px] md:h-[500px] bg-[#0A0F2C] card-lift isolate"
      data-testid={testId}
    >
      <img
        src={img}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.08]"
        loading="lazy"
      />
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ background: "linear-gradient(180deg, rgba(10,15,44,0) 0%, rgba(10,15,44,0.35) 40%, rgba(10,15,44,0.92) 100%)" }}
      />
      {/* Giant watermark number */}
      <div
        className="pointer-events-none absolute -right-2 -top-8 text-white/[0.07] font-extrabold leading-none select-none"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "clamp(140px, 14vw, 180px)" }}
      >
        {String(index).padStart(2, "0")}
      </div>

      <div className="absolute inset-0 p-6 md:p-7 flex flex-col justify-between z-10 text-white">
        <div>
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-[0.18em] border border-white/20">
            {data.tag}
          </span>
        </div>

        <div>
          <h3
            className="text-2xl md:text-[26px] font-extrabold tracking-tight leading-[1.1]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {data.title}
          </h3>
          <p className="mt-2 text-white/85 text-sm leading-relaxed">{data.desc}</p>

          {/* Bullets — always visible on mobile, revealed on desktop hover */}
          <ul className="mt-4 md:mt-0 md:max-h-0 md:opacity-0 md:overflow-hidden md:group-hover:max-h-40 md:group-hover:opacity-100 md:group-hover:mt-4 transition-all duration-500 ease-out flex flex-col gap-1.5">
            {data.bullets.map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-white/90 text-sm">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#FFD84D] text-[#0A0F2C] shrink-0">
                  <Check size={10} strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-5 border-t border-white/15 flex items-center justify-between gap-3">
            <span className="text-[#FFD84D] text-[11px] font-bold tracking-[0.15em] uppercase">{data.price}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 group-hover:bg-[#FFD84D] group-hover:text-[#0A0F2C] text-white backdrop-blur-sm border border-white/20 group-hover:border-[#FFD84D] px-4 py-2 text-xs font-semibold transition-all">
              {data.cta} <ArrowRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}


function FaqAccordionItem({ index, q, a }) {
  return (
    <Accordion type="single" collapsible defaultValue={index === 0 ? `faq-${index}` : undefined} className="w-full">
      <AccordionItem value={`faq-${index}`} className="border-b border-gray-100 last:border-b-0 px-3 md:px-4" data-testid={`faq-item-home-${index}`}>
        <AccordionTrigger className="py-4 md:py-5 text-[15px] md:text-[16px] font-semibold text-[#0A0F2C] hover:no-underline text-left" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {q}
        </AccordionTrigger>
        <AccordionContent className="pb-5 text-[14px] md:text-[15px] text-[#4B5563] leading-relaxed">
          {a}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}


/* ---------- Visuels illustratifs pour "Comment ça marche" ---------- */
function StepVisualConfig() {
  const { t } = useI18n();
  const v = t.advantages.visual.config;
  return (
    <div className="relative h-[160px] md:h-[180px] aspect-[4/3]" aria-hidden="true">
      {/* Faux mini-configurateur flottant — fond sombre pour pop sur card blanche */}
      <div className="absolute inset-0 rounded-2xl bg-[#0A0F2C] p-4 flex flex-col gap-2.5 shadow-[0_20px_50px_-15px_rgba(10,15,44,0.4)] overflow-hidden">
        <div className="pointer-events-none absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#2D5BFF]/40 blur-2xl" />
        <div className="relative flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FFD84D]" />
          <div className="h-2 rounded-full bg-white/25 flex-1" />
        </div>
        <div className="relative rounded-lg bg-white/10 border border-white/10 px-2.5 py-2 flex items-center justify-between">
          <span className="text-white/80 text-[10px] font-semibold uppercase tracking-[0.15em]">{v.service}</span>
          <span className="text-white text-[11px] font-bold">{v.serviceVal}</span>
        </div>
        <div className="relative rounded-lg bg-white/10 border border-white/10 px-2.5 py-2 flex items-center justify-between">
          <span className="text-white/80 text-[10px] font-semibold uppercase tracking-[0.15em]">{v.home}</span>
          <span className="text-white text-[11px] font-bold">{v.homeVal}</span>
        </div>
        <div className="relative rounded-lg bg-white/10 border border-white/10 px-2.5 py-2 flex items-center justify-between">
          <span className="text-white/80 text-[10px] font-semibold uppercase tracking-[0.15em]">{v.date}</span>
          <span className="text-white text-[11px] font-bold">{v.dateVal}</span>
        </div>
        <div className="relative mt-auto rounded-full bg-white/10 border border-white/15 text-white text-[11px] font-bold text-center py-2 tracking-[0.02em] flex items-center justify-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFD84D]" />
          {v.status}
        </div>
      </div>
      <div className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full bg-[#FFD84D] shadow-lg flex items-center justify-center">
        <Settings2 size={18} className="text-[#0A0F2C]" strokeWidth={2.5} />
      </div>
    </div>
  );
}

function StepVisualPrice() {
  const { t } = useI18n();
  const v = t.advantages.visual.price;
  return (
    <div className="relative h-[160px] md:h-[180px] aspect-[4/3]" aria-hidden="true">
      {/* Devis "ticket" — sans prix chiffré, purement décoratif */}
      <div className="absolute inset-0 rounded-2xl bg-white border border-gray-200 p-5 flex flex-col shadow-[0_24px_50px_-20px_rgba(10,15,44,0.3)]">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#2D5BFF] font-extrabold">{v.label}</span>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#EAF0FF] text-[#2D5BFF]">
            <Check size={13} strokeWidth={3} />
          </span>
        </div>
        <div className="mt-2 text-[15px] font-extrabold text-[#0A0F2C] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {v.serviceVal}
        </div>
        {/* Ligne pointillée "ticket" */}
        <div className="relative my-3">
          <div className="border-t border-dashed border-gray-300" />
          <div className="absolute -left-7 -top-2 w-4 h-4 rounded-full bg-[#2D5BFF] border border-[#2D5BFF]" />
          <div className="absolute -right-7 -top-2 w-4 h-4 rounded-full bg-[#2D5BFF] border border-[#2D5BFF]" />
        </div>
        <ul className="flex flex-col gap-2 text-[11px] text-[#0A0F2C]">
          <li className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0">
              <Check size={9} strokeWidth={3.5} />
            </span>
            <span className="font-semibold">{v.line1}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0">
              <Check size={9} strokeWidth={3.5} />
            </span>
            <span className="font-semibold">{v.line2}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0">
              <Check size={9} strokeWidth={3.5} />
            </span>
            <span className="font-semibold">{v.line3}</span>
          </li>
        </ul>
      </div>
      <div className="absolute -bottom-3 -left-3 w-10 h-10 rounded-full bg-[#2D5BFF] shadow-lg flex items-center justify-center">
        <Zap size={18} className="text-white" strokeWidth={2.5} fill="#FFD84D" />
      </div>
    </div>
  );
}

function StepVisualDone() {
  const { t } = useI18n();
  const v = t.advantages.visual.done;
  const days = (t.advantages.visual.done.daysShort) || ["L", "M", "M", "J", "V", "S", "D"];
  return (
    <div className="relative h-[160px] md:h-[180px] aspect-[4/3]" aria-hidden="true">
      {/* Calendrier simplifié — intemporel, sans mois/année */}
      <div className="absolute inset-0 rounded-2xl bg-white border border-gray-100 overflow-hidden shadow-[0_24px_50px_-20px_rgba(10,15,44,0.25)] flex flex-col">
        <div className="bg-[#0A0F2C] px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFD84D]" />
            <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
            <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
          </div>
          <span className="inline-flex items-center gap-1 text-white/85 text-[10px] font-bold uppercase tracking-[0.18em]">
            <Check size={11} strokeWidth={3} className="text-[#FFD84D]" />
            {v.line1}
          </span>
        </div>
        <div className="px-3 pt-2 grid grid-cols-7 gap-1">
          {days.map((d, i) => (
            <div key={i} className="text-[9px] font-bold text-[#4B5563] text-center uppercase">{d}</div>
          ))}
          {Array.from({ length: 14 }).map((_, i) => {
            const isActive = i === 8;
            return (
              <div key={i} className={`aspect-square rounded-md flex items-center justify-center text-[9px] font-bold ${isActive ? "bg-[#2D5BFF] text-white shadow-[0_4px_10px_rgba(45,91,255,0.4)]" : "bg-gray-50 text-[#4B5563]"}`}>
                {isActive ? <Check size={10} strokeWidth={3.5} /> : ""}
              </div>
            );
          })}
        </div>
        <div className="mt-auto px-4 pb-3 pt-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#FFD84D] shrink-0">
              <Check size={9} className="text-[#0A0F2C]" strokeWidth={3.5} />
            </span>
            <span className="text-[10px] font-bold text-[#0A0F2C]">{v.line2}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#FFD84D] shrink-0">
              <Check size={9} className="text-[#0A0F2C]" strokeWidth={3.5} />
            </span>
            <span className="text-[10px] font-bold text-[#0A0F2C]">{v.line3}</span>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full bg-[#FFD84D] shadow-lg flex items-center justify-center">
        <ShieldCheck size={18} className="text-[#0A0F2C]" strokeWidth={2.5} />
      </div>
    </div>
  );
}

/* ---------- Exemple de devis Mopper (carte illustrative, structure réelle) ---------- */
function DevisExampleCard({ ex }) {
  return (
    <div className="relative mx-auto w-full max-w-[460px]" data-testid="howitworks-devis-example">
      <div className="pointer-events-none absolute -inset-6 rounded-[40px] bg-white/10 blur-2xl" aria-hidden="true" />
      <div className="relative rounded-3xl bg-white shadow-[0_40px_80px_-30px_rgba(10,15,44,0.5)] overflow-hidden">
        <div className="p-6 md:p-8">
          {/* En-tête : logo + badge exemple */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="font-extrabold text-[#2D5BFF] text-xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>mopper.</span>
              <div className="mt-1 text-[12px] text-[#9CA3AF] font-medium">{ex.docLabel}</div>
            </div>
            <span className="shrink-0 rounded-md bg-[#EAF0FF] text-[#2D5BFF] text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1">{ex.badge}</span>
          </div>

          {/* Votre demande */}
          <div className="mt-6">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#9CA3AF]">{ex.demandLabel}</div>
            <div className="mt-1.5 font-extrabold text-[#0A0F2C] text-[17px] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{ex.service}</div>
            <div className="mt-1 text-[13px] text-[#6B7280]">{ex.route}</div>
          </div>

          {/* Lignes du devis */}
          <div className="mt-5 flex flex-col">
            {ex.lines.map((l, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-3 border-t border-gray-100">
                <div className="min-w-0">
                  <div className="font-bold text-[#0A0F2C] text-[14px] leading-tight">{l.label}</div>
                  <div className="text-[12px] text-[#9CA3AF] leading-tight mt-0.5">{l.sub}</div>
                </div>
                <div className="shrink-0 text-[13px] font-semibold text-[#4B5563] tabular-nums">{l.amount}</div>
              </div>
            ))}
          </div>

          {/* Prix fixe */}
          <div className="mt-4 pt-4 border-t-2 border-[#0A0F2C]/5 flex items-end justify-between gap-3">
            <div>
              <div className="font-extrabold text-[#0A0F2C] text-[14px]">{ex.priceLabel}</div>
              <div className="text-[11px] text-[#9CA3AF] mt-0.5 max-w-[160px] leading-tight">{ex.priceNote}</div>
            </div>
            <div className="font-extrabold text-[#0A0F2C] text-2xl md:text-[28px] tracking-tight border-b-[3px] border-[#FFD84D] pb-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{ex.priceValue}</div>
          </div>

          {/* Référent */}
          <div className="mt-5 flex items-center gap-2.5 rounded-xl bg-[#F7F9FF] px-4 py-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0">
              <Users size={14} strokeWidth={2.5} />
            </span>
            <span className="text-[13px] text-[#4B5563] font-medium">{ex.referent}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Slider avant/après (mobile) — glisser pour comparer ---------- */
function BeforeAfterSlider({ before, after, beforeLabel, afterLabel, beforeAlt, afterAlt, heightClass = "aspect-[3/4]" }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const update = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(3, Math.min(97, p)));
  };
  return (
    <div
      ref={ref}
      onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); update(e.clientX); }}
      onPointerMove={(e) => { if (dragging.current) update(e.clientX); }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
      className={`relative w-full ${heightClass} rounded-[20px] overflow-hidden select-none touch-none shadow-[0_25px_50px_-15px_rgba(10,15,44,0.35)] cursor-ew-resize`}
      data-testid="compare-slider"
    >
      {/* Base = après (vidée) */}
      <img src={after} alt={afterAlt} draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      {/* Overlay = avant (encombrée), découpé jusqu'à pos% */}
      <img src={before} alt={beforeAlt} draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />

      <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-white/95 backdrop-blur-sm px-2 py-0.5 sm:px-3 sm:py-1.5 text-[9px] sm:text-[11px] font-extrabold uppercase tracking-[0.12em] sm:tracking-[0.14em] text-[#0A0F2C] shadow-[0_8px_20px_-6px_rgba(0,0,0,0.3)]">{beforeLabel}</span>
      <span className="absolute top-3 right-3 inline-flex items-center rounded-full bg-[#FFD84D] px-2 py-0.5 sm:px-3 sm:py-1.5 text-[9px] sm:text-[11px] font-extrabold uppercase tracking-[0.12em] sm:tracking-[0.14em] text-[#0A0F2C] shadow-[0_8px_20px_-6px_rgba(0,0,0,0.3)]">{afterLabel} Mopper</span>

      {/* Poignée */}
      <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[3px] h-full bg-white shadow-[0_0_12px_rgba(0,0,0,0.35)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-[0_8px_24px_-4px_rgba(0,0,0,0.45)] flex items-center justify-center text-[#2D5BFF]">
          <ChevronRight size={14} className="rotate-180 -mr-1" strokeWidth={3} />
          <ChevronRight size={14} className="-ml-1" strokeWidth={3} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Exemple de demande (formulaire) — aperçu interactif étape 1 ---------- */
function StepVisualRequest({ f }) {
  const Field = ({ label, value, icon }) => (
    <div className="mt-4 first:mt-5">
      <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#9CA3AF] mb-1.5">{label}</div>
      <div className="flex items-center justify-between gap-2 rounded-xl bg-[#F7F9FF] border border-[#E6ECFA] px-4 py-3">
        <span className="font-semibold text-[#0A0F2C] text-[14px] truncate">{value}</span>
        {icon}
      </div>
    </div>
  );
  return (
    <div className="relative mx-auto w-full max-w-[460px]" data-testid="howitworks-visual-request">
      <div className="pointer-events-none absolute -inset-6 rounded-[40px] bg-white/10 blur-2xl" aria-hidden="true" />
      <div className="relative rounded-3xl bg-white shadow-[0_40px_80px_-30px_rgba(10,15,44,0.5)] p-6 md:p-8">
        <div className="flex items-center justify-between gap-3">
          <h4 className="font-extrabold text-[#0A0F2C] text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.title}</h4>
          <span className="shrink-0 text-[12px] text-[#9CA3AF] font-semibold">{f.timing}</span>
        </div>

        <Field label={f.serviceLabel} value={f.serviceValue} icon={<ChevronRight size={16} className="text-[#9CA3AF] rotate-90 shrink-0" />} />
        <Field label={f.housingLabel} value={f.housingValue} icon={<ChevronRight size={16} className="text-[#9CA3AF] rotate-90 shrink-0" />} />
        <Field label={f.dateLabel} value={f.dateValue} icon={<Clock size={15} className="text-[#2D5BFF] shrink-0" />} />

        <div className="mt-4">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#9CA3AF] mb-2">{f.photosLabel}</div>
          <div className="flex gap-2.5">
            <span className="w-12 h-12 rounded-xl bg-[#EAF0FF]" />
            <span className="w-12 h-12 rounded-xl bg-[#FFF4CC]" />
            <span className="w-12 h-12 rounded-xl bg-[#F1F3F7]" />
            <span className="w-12 h-12 rounded-xl border-2 border-dashed border-[#D8DEEA] text-[#9CA3AF] flex items-center justify-center text-xl font-light">+</span>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl bg-[#2D5BFF] text-white py-3.5 font-extrabold text-center text-[15px]">{f.submit}</div>
      </div>
    </div>
  );
}





/* ---------- SuiviMockup — aperçu réaliste du /suivi/:token (données fictives) ---------- */
function SuiviMockup({ maxW = "max-w-[360px]" }) {
  const { t } = useI18n();
  const p = t.suiviRc.preview;
  return (
    <div className={`relative mx-auto w-full ${maxW}`} aria-hidden="true" data-testid="suivi-mockup">
      {/* Halo pastel */}
      <div className="pointer-events-none absolute -inset-8 rounded-[48px] bg-gradient-to-br from-[#2D5BFF]/8 via-transparent to-[#FFD84D]/10 blur-2xl" />
      {/* Pastille jaune décorative flottante */}
      <div className="pointer-events-none absolute -top-3 -right-3 w-14 h-14 rounded-full bg-[#FFD84D]/25 blur-md" />
      <div className="pointer-events-none absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-[#2D5BFF]/15 blur-md" />

      {/* Cadre "téléphone" */}
      <div className="relative rounded-[36px] bg-white border border-[#EAF0FF] shadow-[0_40px_80px_-30px_rgba(10,15,44,0.35)] overflow-hidden">
        {/* Barre statut / notch */}
        <div className="h-8 flex items-center justify-center border-b border-gray-100 bg-white">
          <div className="w-24 h-1.5 rounded-full bg-gray-200" />
        </div>

        {/* En-tête compact */}
        <div className="px-5 pt-5 pb-3 bg-white">
          <div className="text-[13px] font-bold text-[#0A0F2C] flex items-center gap-1.5">
            {p.greeting} <span aria-hidden="true">👋</span>
          </div>
          <div className="mt-0.5 text-[12px] text-[#4B5563]">{p.subtitle}</div>
        </div>

        {/* Carte statut principale — ton bleu (TONES.blue de Suivi.jsx) */}
        <div className="mx-5 mt-1 rounded-2xl bg-[#EAF0FF] border border-[#2D5BFF]/15 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2D5BFF] text-white flex items-center justify-center text-lg shrink-0">
              <span aria-hidden="true">{SUIVI_STEP_ICONS.arrived_onsite}</span>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#2D5BFF]">{p.stepsLabel}</div>
              <div className="mt-0.5 font-extrabold text-[15px] text-[#0A0F2C] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {p.currentStatus}
              </div>
              <div className="mt-1 text-[11px] text-[#4B5563]">{p.lastUpdate}</div>
            </div>
          </div>
          {/* Barre de progression */}
          <div className="mt-3 h-1.5 rounded-full bg-white/70 overflow-hidden">
            <div className="h-full w-[75%] rounded-full bg-[#2D5BFF]" />
          </div>
        </div>

        {/* Timeline verticale — 5 étapes */}
        <ol className="mx-5 mt-4 pb-4 list-none p-0 flex flex-col gap-2.5">
          {p.steps.map((s, i) => {
            const isDone = s.state === "done";
            const isCurrent = s.state === "current";
            return (
              <li key={s.key} className="flex items-center gap-3">
                <span
                  className={
                    "inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 " +
                    (isDone
                      ? "bg-[#2D5BFF] text-white"
                      : isCurrent
                      ? "bg-[#EAF0FF] ring-2 ring-[#2D5BFF] text-[#2D5BFF]"
                      : "bg-gray-100 text-gray-400")
                  }
                >
                  {isDone ? (
                    <Check size={12} strokeWidth={3.5} />
                  ) : isCurrent ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2D5BFF]" />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  )}
                </span>
                <span
                  className={
                    "text-[12.5px] leading-snug " +
                    (isDone
                      ? "text-[#0A0F2C] font-medium"
                      : isCurrent
                      ? "text-[#0A0F2C] font-bold"
                      : "text-gray-400 font-medium")
                  }
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>

        {/* Info pratique compacte */}
        <div className="mx-5 mb-4 rounded-xl bg-[#F7F8FC] p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-[11.5px] text-[#0A0F2C]">
            <Clock size={12} className="text-[#2D5BFF] shrink-0" />
            <span className="font-semibold">{p.date}</span>
          </div>
          <div className="flex items-center gap-2 text-[11.5px] text-[#0A0F2C]">
            <Users size={12} className="text-[#2D5BFF] shrink-0" />
            <span className="font-semibold">{p.team}</span>
          </div>
          <div className="flex items-center gap-2 text-[11.5px] text-[#4B5563]">
            <MessageCircle size={12} className="text-[#4B5563] shrink-0" />
            <span>{p.helpLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderTitle(text, highlight, dropOnMobile) {
  if (!text) return text;
  const marks = [];
  if (highlight) {
    const hi = text.toLowerCase().indexOf(highlight.toLowerCase());
    if (hi !== -1) marks.push({ start: hi, end: hi + highlight.length, type: "hi" });
  }
  if (dropOnMobile) {
    const di = text.indexOf(dropOnMobile);
    if (di !== -1) marks.push({ start: di, end: di + dropOnMobile.length, type: "drop" });
  }
  if (!marks.length) return text;
  marks.sort((a, b) => a.start - b.start);
  const out = [];
  let cursor = 0, key = 0;
  for (const m of marks) {
    if (m.start < cursor) continue;
    if (m.start > cursor) out.push(text.slice(cursor, m.start));
    if (m.type === "hi") out.push(<span key={key++} className="text-[#ffea00]">{text.slice(m.start, m.end)}</span>);
    else out.push(<span key={key++} className="hidden md:inline">{text.slice(m.start, m.end)}</span>);
    cursor = m.end;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return <>{out}</>;
}




export default function Home() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [serviceType, setServiceType] = useState("move");
  const [postal, setPostal] = useState("");
  const [hiwStep, setHiwStep] = useState(0);

  // Smooth-scroll to hash target on load / navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t0 = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t0);
    }
  }, [location.hash]);

  function goToQuote(e) {
    e.preventDefault();
    const params = new URLSearchParams();
    if (serviceType) params.set("service", serviceType);
    if (postal) params.set("zip", postal);
    navigate(`/devis?${params.toString()}`);
  }

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      <Seo
        title={DEFAULT_META.title}
        description={DEFAULT_META.description}
        path="/"
        jsonLd={[]}
      />
      <Header />

      {/* HERO — full-bleed blue, edge-to-edge */}
      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden" data-testid="hero-section">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.12), transparent 55%)" }}
        />
        <div className="relative z-10 mx-auto pt-32 md:pt-40 pb-6 md:pb-8 px-6 sm:px-8 md:px-10 lg:px-14" style={{ maxWidth: "1760px" }}>
            <motion.div
              className="max-w-[920px] relative z-10 px-2 md:px-2"
              initial="hidden"
              animate="show"
              variants={{
                hidden: { opacity: 1 },
                show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
              }}
            >
              <motion.h1
                className="text-white font-extrabold leading-[1.08] tracking-tighter text-4xl sm:text-5xl md:text-5xl lg:text-[56px]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 110, damping: 18 } },
                }}
              >
                {renderTitle(t.hero.title1, t.hero.highlight, t.hero.dropOnMobile)}
              </motion.h1>
              <motion.div
                className="flex items-center gap-3 mt-5 md:mt-6"
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 120, damping: 20 } },
                }}
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FFD84D] text-[#0A0F2C] shrink-0">
                  <Heart size={18} fill="#0A0F2C" />
                </span>
                <p className="text-[#FFD84D] text-base md:text-lg lg:text-xl font-semibold">{t.hero.sub}</p>
              </motion.div>

              {/* Reassurance bullets — empilés mobile, en ligne desktop */}
              <motion.ul
                className="mt-6 md:mt-7 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-2 sm:gap-y-2 sm:gap-x-5 text-white/90 text-sm md:text-[15px]"
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 120, damping: 22 } },
                }}
                data-testid="hero-reassurance"
              >
                {t.hero.reassurance.map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[#FFD84D] shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{r}</span>
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            {/* Decorative circles */}
            <div className="pointer-events-none absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/[0.05]" />
            <div className="pointer-events-none absolute right-0 bottom-1/3 w-80 h-80 rounded-full bg-white/[0.04] translate-x-1/4" />

            {/* Team portrait — visible from desktop only (≥ xl = 1280px), always BEHIND content */}
            <div
              className="hidden xl:block pointer-events-none absolute right-0 select-none opacity-70 xl:opacity-100"
              style={{ width: "min(560px, 38vw)", top: "7rem", bottom: 0, zIndex: 0 }}
              aria-hidden="true"
              data-testid="hero-team-portrait"
            >
              {/* Soft glow halo behind the figures */}
              <div
                className="absolute inset-0 -z-10"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 55% at 55% 45%, rgba(255,216,77,0.35), rgba(255,216,77,0) 70%), radial-gradient(ellipse 70% 70% at 50% 60%, rgba(255,255,255,0.18), rgba(255,255,255,0) 75%)",
                  filter: "blur(8px)",
                }}
              />
              <img
                src="https://customer-assets.emergentagent.com/job_mopper-reserve/artifacts/aptlwsv2_Design%20sans%20titre%20%2859%29.png"
                alt={lang === "de" ? "Mopper-Team im Einsatz in der Westschweiz" : lang === "en" ? "Mopper team at work in French-speaking Switzerland" : "Équipe Mopper en intervention en Suisse romande"}
                draggable="false"
                className="relative w-full h-full object-cover object-top block"
                style={{ filter: "drop-shadow(0 30px 40px rgba(10,15,44,0.35))" }}
              />
            </div>

            {/* Configurator INSIDE the blue hero at the bottom */}
            <motion.div
              className="relative z-10 mt-8 md:mt-12 lg:mt-14"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 110, damping: 20, delay: 0.35 }}
            >
              <form
                onSubmit={goToQuote}
                className="bg-white rounded-3xl shadow-[0_24px_48px_-12px_rgba(10,15,44,0.22)] border border-white/20 p-5 md:p-7 max-w-[1040px] mr-auto"
                data-testid="hero-configurator"
              >
                <a
                  href="https://maps.app.goo.gl/AyVkvag8mweWkmtYA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 mb-4 transition-opacity hover:opacity-80"
                  data-testid="hero-google-reviews-link"
                  aria-label="Noté 5 sur 5 par nos clients sur Google — voir les avis"
                >
                  <div className="flex gap-0.5 text-[#ffea00]" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} size={16} fill="#ffea00" stroke="#ffea00" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-[#0A0F2C]" data-testid="hero-trust-badge">
                    {t.hero.trust.split("5/5")[0]}
                    <span className="underline decoration-2 underline-offset-2">5/5</span>
                    {t.hero.trust.split("5/5")[1]}
                  </span>
                </a>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-stretch">
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger
                      className="h-14 rounded-2xl border border-gray-200 bg-white px-5 text-[#0A0F2C] font-medium shadow-none focus:ring-2 focus:ring-[#2D5BFF] focus:ring-offset-0 data-[state=open]:ring-2 data-[state=open]:ring-[#2D5BFF]"
                      data-testid="hero-service-select"
                    >
                      <SelectValue placeholder={t.hero.selectService} />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="move" data-testid="hero-service-option-move">{t.services.move.title}</SelectItem>
                      <SelectItem value="clear" data-testid="hero-service-option-clear">{t.services.clear.title}</SelectItem>
                      <SelectItem value="clean" data-testid="hero-service-option-clean">{t.services.clean.title}</SelectItem>
                      <SelectItem value="transport" data-testid="hero-service-option-transport">{t.services.transport.title}</SelectItem>
                    </SelectContent>
                  </Select>
                  <PostalCodeInput
                    value={postal}
                    onChange={setPostal}
                    placeholder={t.hero.postalCode}
                    className="w-full"
                    inputClassName="w-full h-14 bg-white rounded-2xl border border-gray-200 px-5 text-[#0A0F2C] placeholder:text-gray-400 focus:ring-2 focus:ring-[#2D5BFF] focus:border-transparent outline-none"
                    testId="hero-postal-input"
                  />
                  <button
                    type="submit"
                    className="h-14 px-7 md:px-8 rounded-2xl bg-[#2D5BFF] hover:bg-[#224DE5] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                    data-testid="hero-my-price-button"
                  >
                    {t.cta.book}
                    <ArrowRight size={18} />
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap text-xs md:text-sm">
                  <span className="text-[#2D5BFF] font-semibold" data-testid="hero-pricing-hint">{t.hero.pricingHint} · {t.hero.vatIncluded}</span>
                </div>
              </form>
            </motion.div>
        </div>
      </section>

      {/* SERVICES — Batmaid-style stack: 2 cols desktop / 1 col mobile, max 2 visible per row */}
      <section className="py-10 md:py-16 overflow-x-clip px-6 sm:px-8 md:px-10 lg:px-14" id="services">
        <div className="mx-auto" style={{ maxWidth: "1760px" }}>
          <div className="flex items-end justify-between gap-6 mb-8 md:mb-12">
            <h2
              className="text-[#0A0F2C] font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="services-heading"
            >
              {t.services.heading}.
            </h2>
            <Link to="/devis" className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#0A0F2C]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#0A0F2C] hover:border-[#2D5BFF] hover:text-[#2D5BFF] transition-colors shrink-0" data-testid="services-cta">
              {t.cta.myPrice} <ArrowRight size={14} />
            </Link>
          </div>
          <p className="text-[#4B5563] text-base md:text-lg max-w-xl mb-10 md:mb-14">{t.services.sub}</p>

          <ServicesStackScroller
            items={[
              { img: CARD_IMG_MOVE, data: t.services.move, to: "/devis?service=move", testId: "service-card-move", kind: "b2c" },
              { img: CARD_IMG_CLEAR, data: t.services.clear, to: "/devis?service=clear", testId: "service-card-clear", kind: "b2c" },
              { img: CARD_IMG_CLEAN, data: t.services.clean, to: "/devis?service=clean", testId: "service-card-clean", kind: "b2c" },
              { img: CARD_IMG_TRANSPORT, data: t.services.transport, to: "/devis?service=transport", testId: "service-card-transport", kind: "b2c" },
            ]}
            navigate={navigate}
          />
        </div>
      </section>

      {/* HOW IT WORKS — full-bleed blue background */}
      <section className="relative bg-[#2D5BFF] hero-noise py-16 md:py-28 overflow-hidden" id="how-it-works">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="pointer-events-none absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 -bottom-32 w-[400px] h-[400px] rounded-full bg-[#FFD84D]/20 blur-3xl" />

        <div className="relative px-4 sm:px-6 md:px-10 lg:px-14">
          <div className="mx-auto md:rounded-[32px] md:bg-white/[0.06] md:border md:border-white/15 md:px-12 md:py-12 lg:px-16 lg:py-16" style={{ maxWidth: "1760px" }}>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT — titre, étapes interactives, CTA */}
            <div className="order-1">
            {/* Intro */}
            <div className="mb-8 md:mb-10">
              <h2
                className="text-white font-extrabold tracking-tight leading-[1.04]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
                data-testid="howitworks-heading"
              >
                {t.howItWorks.heading}.
              </h2>
              <p
                className="mt-4 text-white/85 leading-relaxed max-w-md"
                style={{ fontSize: "clamp(1rem, 1.15vw, 1.1875rem)" }}
                data-testid="howitworks-sub"
              >
                {t.howItWorks.sub}
              </p>
            </div>

            {/* Étapes interactives — cliquables pour changer l'aperçu */}
            <div className="flex flex-col gap-3" role="tablist" aria-label={t.howItWorks.heading} data-testid="howitworks-row">
              {t.howItWorks.steps.map((s, i) => {
                const activeStep = hiwStep === i;
                return (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={activeStep}
                    aria-controls="howitworks-preview"
                    onClick={() => setHiwStep(i)}
                    className={`text-left rounded-2xl px-4 py-4 sm:px-5 transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                      activeStep
                        ? "bg-white shadow-[0_18px_45px_-18px_rgba(0,0,0,0.55)]"
                        : "bg-transparent border border-white/20 hover:border-white/50"
                    }`}
                    data-testid={`howitworks-step-${i}`}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center font-extrabold text-[13px] tabular-nums transition-colors duration-200 ${
                          activeStep ? "bg-[#FFD84D] text-[#0A0F2C]" : "border border-white/45 text-white"
                        }`}
                        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className={`flex-1 min-w-0 font-bold tracking-tight text-[16px] md:text-[18px] leading-snug ${activeStep ? "text-[#0A0F2C]" : "text-white"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {s.title}
                      </h3>
                      <span className={`shrink-0 text-[12px] md:text-[13px] font-semibold whitespace-nowrap ${activeStep ? "text-[#2D5BFF]" : "text-white/60"}`}>
                        {t.howItWorks.timings[i]}
                      </span>
                    </div>
                    {activeStep && (
                      <p className="mt-2.5 pl-[3.25rem] text-[#4B5563] text-[14px] md:text-[15px] leading-relaxed">
                        {s.desc}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {/* CTA desktop — sous les étapes (colonne gauche) */}
            <div className="mt-8 hidden lg:flex flex-row items-center gap-4" data-testid="howitworks-cta-block">
              <button
                onClick={() => navigate("/devis")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD84D] hover:bg-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white text-[#0A0F2C] px-7 py-4 font-extrabold text-base active:scale-[0.98] transition-colors duration-200 shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]"
                data-testid="howitworks-cta-button"
              >
                {t.howItWorks.ctaButton}
                <ArrowRight size={18} />
              </button>
              <a
                href="https://wa.me/41782321850?text=Bonjour%20Mopper%2C%20j%27ai%20une%20question%20sur%20votre%20service"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center justify-center gap-1.5 text-white font-semibold text-[15px] underline underline-offset-4 decoration-white/40 hover:decoration-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white rounded-md transition-colors duration-200"
                data-testid="howitworks-cta-whatsapp"
              >
                <MessageCircle size={15} />
                {t.howItWorks.ctaWhatsapp}
              </a>
            </div>
            </div>
            {/* RIGHT — aperçu interactif + CTA sous la carte (mobile uniquement) */}
            <div className="order-2" id="howitworks-preview" data-testid="howitworks-preview">
              <div className="w-full mx-auto lg:ml-auto lg:mr-0 max-w-[500px] lg:min-h-[620px] flex items-center justify-center">
                <div key={hiwStep} className="howitworks-preview-fade w-full">
                  {hiwStep === 0 && <StepVisualRequest f={t.howItWorks.form} />}
                  {hiwStep === 1 && <DevisExampleCard ex={t.howItWorks.example} />}
                  {hiwStep === 2 && <SuiviMockup />}
                </div>
              </div>
              {/* CTA — bouton principal + WhatsApp (sous la carte, mobile) */}
              <div className="mt-10 flex lg:hidden flex-col sm:flex-row sm:items-center sm:justify-center gap-4" data-testid="howitworks-cta-block-mobile">
                <button
                  onClick={() => navigate("/devis")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD84D] hover:bg-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base active:scale-[0.98] transition-colors duration-200 shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]"
                  data-testid="howitworks-cta-button-mobile"
                >
                  {t.howItWorks.ctaButton}
                  <ArrowRight size={18} />
                </button>
                <a
                  href="https://wa.me/41782321850?text=Bonjour%20Mopper%2C%20j%27ai%20une%20question%20sur%20votre%20service"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-1.5 text-white font-semibold text-[14px] md:text-[15px] underline underline-offset-4 decoration-white/40 hover:decoration-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white rounded-md transition-colors duration-200"
                  data-testid="howitworks-cta-whatsapp-mobile"
                >
                  <MessageCircle size={15} />
                  {t.howItWorks.ctaWhatsapp}
                </a>
              </div>
            </div>

            </div>
          </div>
        </div>

        {/* ZONE D'INTERVENTION — 6 cantons romands */}
        <div className="relative px-6 sm:px-8 md:px-10 lg:px-14 mt-16 md:mt-24" id="zones" data-testid="zone-section">
          <div className="mx-auto" style={{ maxWidth: "1760px" }}>
          <div className="flex items-end justify-between gap-6 mb-8 md:mb-10">
            <div>
              <h3 className="text-white font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="zone-heading">
                {t.howItWorks.zone.heading}.
              </h3>
              <p className="mt-3 text-white/85 text-base md:text-lg max-w-2xl">{t.howItWorks.zone.sub}</p>
            </div>
          </div>
          </div>
        </div>
        <CantonsScroller items={CANTONS} lang={lang} dotColor="white" />
      </section>

      {/* SUIVI MOPPER + RC PROFESSIONNELLE — respiration blanche après le bleu */}
      <section id="suivi-mopper" className="relative bg-[#F7F8FC] py-16 md:py-24 overflow-hidden">
        {/* Décor subtil */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#2D5BFF]/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#FFD84D]/8 blur-3xl" />

        <div className="relative mx-auto px-4 sm:px-6 md:px-10 lg:px-14" style={{ maxWidth: "1760px" }}>
          <div className="md:rounded-[32px] md:bg-white md:border md:border-[#EAF0FF] md:shadow-[0_30px_70px_-35px_rgba(10,15,44,0.2)] md:px-12 md:py-12 lg:px-16 lg:py-16">
          {/* Bloc principal : texte + aperçu mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
            {/* Colonne texte */}
            <div>
              <h2
                className="text-[#0A0F2C] font-extrabold tracking-tight leading-[1.05]"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "clamp(1.75rem, 3.6vw, 3rem)" }}
                data-testid="suivi-heading"
              >
                {t.suiviRc.heading}
              </h2>
              <p
                className="mt-4 text-[#4B5563] leading-relaxed"
                style={{ fontSize: "clamp(1rem, 1.1vw, 1.125rem)" }}
                data-testid="suivi-sub"
              >
                <span className="sm:hidden">{t.suiviRc.subMobile}</span>
                <span className="hidden sm:inline">{t.suiviRc.sub}</span>
              </p>

              <ul className="mt-6 hidden lg:flex flex-col gap-2.5" data-testid="suivi-benefits">
                {t.suiviRc.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-[#0A0F2C]">
                    <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D5BFF] text-white shrink-0">
                      <Check size={12} strokeWidth={3.5} />
                    </span>
                    <span className="font-medium leading-snug">{b}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate("/devis")}
                className="mt-8 hidden lg:inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD84D] hover:bg-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2D5BFF] text-[#0A0F2C] px-7 py-4 font-extrabold text-base active:scale-[0.98] transition-colors duration-200 shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]"
                data-testid="suivi-cta"
              >
                {t.suiviRc.cta}
                <ArrowRight size={18} />
              </button>
            </div>

            {/* Colonne aperçu mobile */}
            <div id="suivi-preview" className="relative lg:pl-4" data-testid="suivi-preview-wrap">
              <div className="mx-auto lg:ml-auto lg:mr-0 w-full max-w-[360px] lg:max-w-[440px]">
                <SuiviMockup maxW="max-w-none" />
              </div>

              {/* Mobile uniquement : bénéfices sous l'aperçu + CTA */}
              <div className="lg:hidden mt-8">
                <ul className="flex flex-col gap-2.5" data-testid="suivi-benefits-mobile">
                  {t.suiviRc.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-[#0A0F2C]">
                      <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D5BFF] text-white shrink-0">
                        <Check size={12} strokeWidth={3.5} />
                      </span>
                      <span className="font-medium leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate("/devis")}
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD84D] hover:bg-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2D5BFF] text-[#0A0F2C] px-7 py-4 font-extrabold text-base active:scale-[0.98] transition-colors duration-200 shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]"
                  data-testid="suivi-cta-mobile"
                >
                  {t.suiviRc.cta}
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>



      {/* COMPARE — carte blanche flottante sur fond bleu Mopper (comme la section Équipe) */}
      <section id="compare" className="relative bg-[#2D5BFF] hero-noise py-16 md:py-24 lg:py-28 px-6 sm:px-8 md:px-10 lg:px-14 overflow-hidden" data-testid="compare-section">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-[#FFD84D]/20 blur-3xl" />
        <div className="relative mx-auto" style={{ maxWidth: "1360px" }}>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 lg:gap-8 items-stretch">
              <div className="relative rounded-[20px] md:rounded-[28px] bg-white p-7 md:p-10 lg:p-12 shadow-[0_25px_50px_-15px_rgba(10,15,44,0.25)]">
                <h2
                  className="text-[#0A0F2C] font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  data-testid="advantages-compare-heading"
                >
                  {t.advantages.compare.heading}
                </h2>
                <p className="mt-4 text-[#4B5563] text-base md:text-lg max-w-md">{t.advantages.compare.sub}</p>

                {/* Slider avant/après — mobile uniquement */}
                <div className="mt-8 lg:hidden">
                  <BeforeAfterSlider
                    before={IMG_COMPARE_BEFORE}
                    after={IMG_COMPARE_AFTER}
                    beforeLabel={t.advantages.compare.beforeTitle}
                    afterLabel={t.advantages.compare.afterTitle}
                    beforeAlt={lang === "de" ? "Vollgestellter Keller vor der Mopper-Entrümpelung" : lang === "en" ? "Cluttered cellar before Mopper clear-out" : "Cave encombrée avant le débarras Mopper"}
                    afterAlt={lang === "de" ? "Leerer, sauberer Keller nach der Mopper-Entrümpelung" : lang === "en" ? "Empty, clean cellar after Mopper clear-out" : "Cave vidée et propre après le débarras Mopper"}
                  />
                </div>

                {/* Avant */}
                <div className="mt-10 hidden lg:block rounded-2xl bg-[#FAF7F2] border border-[#0A0F2C]/[0.06] p-6" data-testid="advantages-before">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF] font-bold">{t.advantages.compare.beforeTitle}</span>
                    <span className="h-px flex-1 bg-[#0A0F2C]/[0.08]" />
                    <span className="text-xs font-semibold text-[#9CA3AF]">{t.advantages.compare.before.label}</span>
                  </div>
                  <ul className="flex flex-col gap-2.5">
                    {t.advantages.compare.before.points.map((p, i) => (
                      <li key={i} className="flex items-center gap-3 text-[15px] text-[#4B5563]">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-[#9CA3AF] shrink-0">
                          <X size={11} strokeWidth={3} />
                        </span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Après */}
                <div className="mt-4 hidden lg:block rounded-2xl bg-[#0A0F2C] hero-noise text-white p-6 overflow-hidden relative" data-testid="advantages-after">
                  <div className="pointer-events-none absolute -right-10 -bottom-10 w-32 h-32 rounded-full bg-[#FFD84D]/30 blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFD84D] font-bold">{t.advantages.compare.afterTitle}</span>
                      <span className="h-px flex-1 bg-white/20" />
                      <span className="text-xs font-semibold text-white/85">{t.advantages.compare.after.label}</span>
                    </div>
                    <ul className="flex flex-col gap-2.5">
                      {t.advantages.compare.after.points.map((p, i) => (
                        <li key={i} className="flex items-center gap-3 text-[15px] text-white/95">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FFD84D] text-[#0A0F2C] shrink-0">
                            <Check size={11} strokeWidth={3} />
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Desktop — un seul slider avant/après, à la hauteur du bloc */}
              <div className="hidden lg:block h-full min-h-[560px]" data-testid="compare-photos">
                <BeforeAfterSlider
                  before={IMG_COMPARE_BEFORE}
                  after={IMG_COMPARE_AFTER}
                  beforeLabel={t.advantages.compare.beforeTitle}
                  afterLabel={t.advantages.compare.afterTitle}
                  beforeAlt={lang === "de" ? "Vollgestellter Keller vor der Mopper-Entrümpelung" : lang === "en" ? "Cluttered cellar before Mopper clear-out" : "Cave encombrée avant le débarras Mopper"}
                  afterAlt={lang === "de" ? "Leerer, sauberer Keller nach der Mopper-Entrümpelung" : lang === "en" ? "Empty, clean cellar after Mopper clear-out" : "Cave vidée et propre après le débarras Mopper"}
                  heightClass="h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* BLUE BLOCK — Team + CtaFinal partagent un fond bleu unique (conclusion de page) */}
        <div className="relative bg-[#2D5BFF] hero-noise overflow-hidden text-white">
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
          <div className="pointer-events-none absolute -right-32 top-0 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-32 top-1/3 w-[400px] h-[400px] rounded-full bg-[#FFD84D]/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-0 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl" />

        {/* TEAM — Mopper respecte ses employés (carte blanche sur fond bleu, comme Compare) */}
        <section className="relative pt-16 md:pt-24 pb-12 md:pb-20 px-6 sm:px-8 md:px-10 lg:px-14" data-testid="team-section">
          <div className="relative mx-auto" style={{ maxWidth: "1760px" }}>
            <div className="relative rounded-[20px] md:rounded-[28px] bg-white p-7 md:p-10 lg:p-14 shadow-[0_25px_50px_-15px_rgba(10,15,44,0.25)]">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-start">
                {/* Header column */}
                <div>
                  <h2
                    className="text-[#0A0F2C] font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    data-testid="team-heading"
                  >
                    {t.team.heading}
                    <br />
                    <span className="text-[#2D5BFF]">{t.team.heading2}</span>
                  </h2>
                  <p className="mt-5 text-[#4B5563] text-base md:text-lg max-w-md">{t.team.sub}</p>

                  {/* Trust chips — qualitative, no absolute numbers */}
                  <ul className="mt-8 md:mt-10 flex flex-col gap-2.5" data-testid="team-trust">
                    {(t.team.trust || []).map((it, i) => (
                      <li key={i} className="flex items-start gap-3 text-[14px] md:text-[15px] text-[#0A0F2C]" data-testid={`team-trust-${i}`}>
                        <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0">
                          <ShieldCheck size={13} strokeWidth={2.5} />
                        </span>
                        <span className="font-semibold leading-snug">{it.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Items grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4" data-testid="team-items">
                  {t.team.items.map((item, i) => {
                    const icons = [
                      <Users size={20} />,
                      <Truck size={20} />,
                      <Shield size={20} />,
                      <Recycle size={20} />,
                      <MessageSquare size={20} />,
                      <Heart size={20} fill="#0A0F2C" />,
                    ];
                    return (
                      <article
                        key={i}
                        className="rounded-2xl bg-[#FAF7F2] border border-[#0A0F2C]/[0.06] p-5 md:p-6 hover:bg-[#F0EBE2] transition-colors"
                        data-testid={`team-item-${i}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[#FFD84D] inline-flex items-center justify-center text-[#0A0F2C] shadow-[0_8px_20px_-6px_rgba(255,216,77,0.5)]">
                          {icons[i]}
                        </div>
                        <h3 className="mt-4 font-extrabold text-[#0A0F2C] text-lg md:text-xl tracking-tight leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{item.desc}</p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="relative pb-16 md:pb-24 lg:pb-28 pt-4 md:pt-8 px-6 sm:px-8 md:px-10 lg:px-14" data-testid="cta-final-section">
          <div className="relative z-10 mx-auto" style={{ maxWidth: "1760px" }}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <div className="max-w-3xl">
                <h3 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t.ctaFinal.heading}
                  <br />
                  <span className="text-[#FFD84D]">{t.ctaFinal.heading2}</span>
                </h3>
                <div className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-2 text-white/95 text-sm md:text-base font-semibold" data-testid="cta-final-reassure">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FFD84D] text-[#0A0F2C] shrink-0">
                    <Check size={11} strokeWidth={3} />
                  </span>
                  {t.ctaFinal.reassure}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <button
                  onClick={() => navigate("/devis")}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] text-[#0A0F2C] px-8 py-4 font-bold text-base md:text-lg hover:bg-white transition-all active:scale-[0.98] shadow-[0_12px_30px_-8px_rgba(255,216,77,0.6)]"
                  data-testid="cta-final-primary"
                >
                  {t.ctaFinal.primary} <ArrowRight size={18} />
                </button>
                <a
                  href="https://wa.me/41782321850?text=Bonjour%20Mopper%2C%20je%20vous%20envoie%20quelques%20photos%20pour%20un%20devis"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white border border-white/30 px-7 py-4 font-semibold text-base hover:bg-white/20 transition-all backdrop-blur-sm"
                  data-testid="cta-final-secondary"
                >
                  <MessageCircle size={18} />
                  {t.ctaFinal.secondary}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
